import { api } from "@/lib/api-client";
import { addToast, Button, Progress } from "@heroui/react";
import { useForm } from "@mantine/form";
import { useRef, useState } from "react";
import { LimeInput } from "./LimeInput";
import { LimeAlert } from "./LimeAlert";
import { CloudUpload } from "lucide-react";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";

// because we get a CORS error when using axios for uploading files
function uploadFileWithXHR(
  file: File,
  presignedUrl: string,
  onProgress: (percent: number) => void,
) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Track progress
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress?.(percent);
      }
    });

    // Success/error listeners
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response);
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });
    xhr.addEventListener("error", reject);

    // Set up request
    xhr.open("PUT", presignedUrl);
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.send(file);
  });
}

export const CustomerFileUploadForm = ({
  onSuccessfulUpload,
  customerId,
  userAppointmentId,
}: {
  onSuccessfulUpload?: () => void;
  customerId: number;
  userAppointmentId?: number;
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    mutateAsync: getCustomerFileUploadPresignedUrl,
    isPending: isGettingPresignedUrl,
    processedErrorMessage: getPresignedUrlError,
  } = api.storage.useGetCustomerFileUploadPresignedUrl();

  const {
    mutateAsync: completeCustomerFileUpload,
    isPending: isCompletingCustomerFileUpload,
    processedErrorMessage: completeCustomerFileUploadError,
  } = api.storage.useCompleteCustomerFileUpload();

  const form = useForm<{
    filePath: string;
    label: string;
  }>({
    initialValues: {
      filePath: "",
      label: "",
    },

    validate: {
      filePath: (value) => {
        if (!value) {
          return t`Datoteka je obvezna`;
        }
      },
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    const formData = new FormData(e.target as HTMLFormElement);
    const file = formData.get("fileItem") as File;
    const label = formData.get("fileLabel") as string;

    if (!file) {
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const presignedUrlData = await (async () => {
      try {
        const response = await getCustomerFileUploadPresignedUrl({
          customerId,
          query: {
            contentType: file.type,
            fileNameWithExtension: file.name,
            userAppointmentId,
          },
        });

        return response;
      } catch (error) {
        addToast({
          title: t`Napaka`,
          description: t`Prišlo je do napake pri nalaganju datoteke`,
          color: "danger",
        });

        setIsUploading(false);
        setUploadProgress(0);
        onSuccessfulUpload?.();
        form.reset();

        return null;
      }
    })();

    if (!presignedUrlData) {
      return;
    }

    try {
      await uploadFileWithXHR(
        file,
        presignedUrlData.presignedUrl,
        (percent) => {
          setUploadProgress(percent);
        },
      );

      await completeCustomerFileUpload({
        customerId,
        body: {
          fileId: presignedUrlData.fileId,
          isSuccess: true,
          label,
        },
      });

      addToast({
        title: t`Datoteka naložena`,
        description: t`Datoteka je bila uspešno naložena`,
        color: "success",
      });

      form.reset();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      onSuccessfulUpload?.();
    } catch (error) {
      addToast({
        title: t`Napaka`,
        description: t`Prišlo je do napake pri nalaganju datoteke`,
        color: "danger",
      });

      await completeCustomerFileUpload({
        customerId,
        body: {
          fileId: presignedUrlData.fileId,
          isSuccess: false,
        },
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      onSuccessfulUpload?.();
      form.reset();
    }
  };

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();

        form.validate();

        if (form.isValid()) {
          handleSubmit(e);
        }
      }}
    >
      <LimeInput
        type="file"
        name="fileItem"
        ref={fileInputRef}
        onChange={(e) => {
          form.setFieldValue("filePath", e.target.files?.[0]?.name ?? "");
        }}
        error={form.errors.filePath}
      />

      {isUploading && (
        <Progress
          aria-label="Uploading..."
          value={uploadProgress}
          showValueLabel
          className="mb-2"
          color="primary"
        />
      )}

      <LimeInput
        type="text"
        name="fileLabel"
        label={t`Naziv datoteke`}
        {...form.getInputProps("label")}
      />

      <LimeAlert
        className="mb-1"
        message={getPresignedUrlError ?? completeCustomerFileUploadError}
      />

      <Button
        color="primary"
        variant="bordered"
        startContent={<CloudUpload size={16} />}
        type="submit"
        key={"upload-customer-file-button"}
        isLoading={
          isGettingPresignedUrl || isCompletingCustomerFileUpload || isUploading
        }
      >
        <Trans>Naloži datoteko</Trans>
      </Button>
    </form>
  );
};
